import { linkOptions } from '@tanstack/react-router';
import { useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import { UserRole } from '../../../types/UserRole';
import { Breadcrumbs } from '../../cdl/Breadcrumbs/Breadcrumbs';
import { Button } from '../../cdl/Button/Button';
import { useDrawer } from '../../cdl/Drawer/useDrawer.hook';
import { Text } from '../../cdl/Text/Text';
import { Tooltip } from '../../cdl/Tooltip/Tooltip';
import { ButtonGroup } from '../../common/buttons/ButtonGroup';
import { DataSheet } from '../../common/DataSheet/DataSheet';
import { AdvancedError } from '../../common/Error/AdvancedError';
import { identifyUserRole } from '../../common/helpers/identifyUserRole.helper';
import { translate } from '../../common/helpers/translate.helper';
import { useDocumentTitle } from '../../common/hooks/useDocumentTitle';
import { useRole } from '../../common/hooks/useRole';
import { Page } from '../../common/Page/Page';
import { PageGrid } from '../../common/PageGrid';
import { Box } from '../../common/ui/Box';
import { EditUser } from '../components/EditUser';
import { MfaSection } from '../detail/components/mfa/MfaSection';
import { SetNewPasswordPopup } from '../detail/components/SetNewPasswordPopup';
import { useMfaAccessControl } from '../detail/hooks/useMfaAccessControl';
import { useUser } from '../detail/hooks/useUser';

import { PersonalMemberSection } from './components/PersonalMemberSection';
import { PersonalRoleSection } from './components/PersonalRoleSection';

export const PersonalDetailsPage = () => {
    const role = useRole();
    const userQuery = useUser();
    const mfaAccessControl = useMfaAccessControl(role.user.id);
    const { push, pop } = useDrawer();
    const [isNewPasswordPopupOpen, setIsNewPasswordPopupOpen] = useState<boolean>(false);

    useDocumentTitle(translate('personalDetails.title'));

    const handleEditClick = () => {
        if (userQuery.data) {
            const drawerId = push({
                header: { title: translate('personalDetails.editTitle') },
                content: (
                    <EditUser
                        user={userQuery.data}
                        onSuccess={() => pop(drawerId)}
                        primaryActionText={translate('personalDetails.update')}
                    />
                ),
            });
        }
    };

    if (userQuery.isError) {
        return (
            <Page>
                <AdvancedError />
            </Page>
        );
    }

    const breadcrumbLinks = [{ label: translate('global.settings'), linkOptions: linkOptions({ to: '/settings' }) }];

    const userRole = identifyUserRole(role.user.accessLevel, role.user.companyAccesses);
    const isGroupAdmin = userRole === UserRole.GROUP_ADMIN;
    const companies = !isGroupAdmin ? role.companies : [];
    const isSsoEnabled = userQuery.data?.ssoEnabled;

    return (
        <Page>
            <PageGrid>
                <Box display="flex" flexDirection="column" gap={4}>
                    {role.hasAnyAdminRights() ? <Breadcrumbs links={breadcrumbLinks} /> : null}

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Text as="h1" variant="headline">
                            {translate('personalDetails.title')}
                        </Text>
                        <ButtonGroup>
                            {userQuery.isSuccess ? (
                                <>
                                    {isSsoEnabled ? (
                                        <Tooltip label={translate('user.ssoManagedTooltip')}>
                                            <Button disabled>{translate('personalDetails.changePassword')}</Button>
                                        </Tooltip>
                                    ) : (
                                        <Button onClick={() => setIsNewPasswordPopupOpen(true)}>
                                            {translate('personalDetails.changePassword')}
                                        </Button>
                                    )}
                                    {isSsoEnabled ? (
                                        <Tooltip label={translate('user.ssoManagedTooltip')}>
                                            <Button disabled>{translate('personalDetails.edit')}</Button>
                                        </Tooltip>
                                    ) : (
                                        <Button onClick={handleEditClick}>{translate('personalDetails.edit')}</Button>
                                    )}
                                </>
                            ) : (
                                <>
                                    <Skeleton height="24px" width="120px" />
                                    <Skeleton height="24px" width="50px" />
                                </>
                            )}
                        </ButtonGroup>
                    </Box>
                </Box>

                <Box display="flex" flexDirection="column" gap={40}>
                    {userQuery.isSuccess ? (
                        <PersonalMemberSection user={userQuery.data} />
                    ) : (
                        <DataSheet.Skeleton rows={16} />
                    )}
                    {userQuery.isSuccess && !isSsoEnabled ? (
                        mfaAccessControl.canViewMfa ? (
                            <MfaSection userId={userQuery.data.id} accessControl={mfaAccessControl} />
                        ) : null
                    ) : userQuery.isPending ? (
                        <Box>
                            <Skeleton height="40px" />
                        </Box>
                    ) : null}
                    {!role.isSuperAdmin() ? (
                        userQuery.isSuccess ? (
                            <PersonalRoleSection
                                accessLevel={role.user.accessLevel}
                                companyAccesses={role.companyAccesses}
                                companies={companies}
                                group={role.group}
                            />
                        ) : (
                            <Skeleton height="40px" />
                        )
                    ) : null}
                </Box>

                {userQuery.isSuccess && !isSsoEnabled ? (
                    <SetNewPasswordPopup
                        user={userQuery.data}
                        isOpen={isNewPasswordPopupOpen}
                        onDismiss={() => setIsNewPasswordPopupOpen(false)}
                    />
                ) : null}
            </PageGrid>
        </Page>
    );
};
